'use client';

import React, { useCallback, useMemo } from 'react';
import Link, { LinkProps } from 'next/link';
import { useSearchParams } from 'next/navigation';
import { REGION_PREFIXED_PATHS } from 'lib/region/regionLocale';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { FORWARDED_PARAM_KEYS } from 'lib/analytics/forwardedParams';

export { FORWARDED_PARAM_KEYS };

type HrefType = LinkProps['href'];
type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;

export type CustomLinkProps = React.PropsWithChildren<
  Omit<AnchorProps, keyof LinkProps> &
    Omit<LinkProps, 'href'> & {
      href: HrefType;
      discardParams?: string[];
      appendToExternal?: boolean;
    }
>;

function isExternalHref(href: string): boolean {
  if (href.startsWith('#') || href.startsWith('/')) return false;
  if (href.startsWith('mailto:') || href.startsWith('tel:')) return true;
  // protocol-relative or absolute URL
  return /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(href);
}

/**
 * True for the on-domain Hugo blog (reverse-proxied at /blog). Forwarded
 * tracking params must NOT ride onto internal /blog links: the blog shares the
 * site's GA4, so forwarding internal-nav utm_* would overwrite the visitor's
 * real acquisition source with an internal self-referral and corrupt
 * source/medium; affiliate/click-id params have no consumer on the static blog.
 * Exact-prefix match so paths like /blogger are not caught.
 */
function isBlogPath(pathname: string): boolean {
  return pathname === '/blog' || pathname.startsWith('/blog/');
}

/**
 * Prepend the region slug to internal paths that are in REGION_PREFIXED_PATHS,
 * and to the root path '/' so home links keep the chosen region instead of
 * bouncing through geo-detect. Skips external URLs, already-prefixed paths,
 * and non-customer-facing paths.
 */
export function addRegionPrefix(href: string, regionSlug: string): string {
  if (!href.startsWith('/')) return href; // external or relative
  const firstSegment = href.split('/').filter(Boolean)[0];
  if (!firstSegment) return regionSlug ? `/${regionSlug}/` : href;
  // Don't prefix if it already has a region prefix (2-letter or 2-letter-2/3-letter pattern)
  if (/^\w{2}(-\w{2,3})?$/.test(firstSegment)) return href;
  // Only prefix customer-facing paths
  if (REGION_PREFIXED_PATHS.has(firstSegment.toLowerCase())) {
    return `/${regionSlug}${href}`;
  }
  return href;
}

function addRegionPrefixToHref(
  href: LinkProps['href'],
  regionSlug: string,
): LinkProps['href'] {
  if (typeof href === 'string') {
    return addRegionPrefix(href, regionSlug);
  }
  // UrlObject
  if (href.pathname && !isExternalHref(href.pathname)) {
    return { ...href, pathname: addRegionPrefix(href.pathname, regionSlug) };
  }
  return href;
}

function normalizeHash(hash: string | undefined): string {
  if (!hash) return '';
  return hash.startsWith('#') ? hash : `#${hash}`;
}

function buildExternalUrl(
  baseHref: string,
  options?: {
    incomingQuery?: Record<string, string | string[]>;
    extraParams?: Record<string, string | string[]>;
    appendHash?: string;
  },
): string {
  const isProtocolRelative = baseHref.startsWith('//');
  try {
    if (isProtocolRelative) {
      const url = new URL(`https:${baseHref}`);

      const incomingQuery = options?.incomingQuery ?? {};
      // Apply incoming query (override existing values)
      Object.entries(incomingQuery).forEach(([key, value]) => {
        url.searchParams.delete(key);
        if (Array.isArray(value)) {
          value.forEach((v) => url.searchParams.append(key, v));
        } else {
          url.searchParams.append(key, value);
        }
      });

      const extraParams = options?.extraParams ?? {};
      // Append extra params if not already present
      Object.entries(extraParams).forEach(([key, value]) => {
        if (url.searchParams.has(key)) return;
        if (Array.isArray(value)) {
          value.forEach((v) => url.searchParams.append(key, v));
        } else {
          url.searchParams.append(key, value);
        }
      });

      return `//${url.host}${url.pathname}${url.search}${url.hash}`;
    }

    // Absolute URL path
    const url = new URL(baseHref);

    const incomingQuery = options?.incomingQuery ?? {};
    // Apply incoming query (override existing values)
    Object.entries(incomingQuery).forEach(([key, value]) => {
      url.searchParams.delete(key);
      if (Array.isArray(value)) {
        value.forEach((v) => url.searchParams.append(key, v));
      } else {
        url.searchParams.append(key, value);
      }
    });

    const extraParams = options?.extraParams ?? {};
    // Append extra params if not already present
    Object.entries(extraParams).forEach(([key, value]) => {
      if (url.searchParams.has(key)) return;
      if (Array.isArray(value)) {
        value.forEach((v) => url.searchParams.append(key, v));
      } else {
        url.searchParams.append(key, value);
      }
    });

    const result = url.toString();

    // Maintain existing behavior: only append provided hash for non-protocol-relative
    if (options?.appendHash) {
      return `${result}${normalizeHash(options.appendHash)}`;
    }

    return result;
  } catch {
    return baseHref;
  }
}

export function formatLink(
  href: LinkProps['href'],
  sourceQuery: Record<string, unknown> = {},
  options?: {
    discardParams?: string[];
    appendToExternal?: boolean;
    asObject?: boolean; // false => string (default), true => href object
  },
): LinkProps['href'] | string {
  const discardParams = options?.discardParams ?? [];
  const appendToExternal = options?.appendToExternal ?? false;
  const asObject = options?.asObject ?? false;

  // Compute allowed keys set
  const discarded = new Set<string>(discardParams.map((k) => k.toLowerCase()));
  const allowedKeys = FORWARDED_PARAM_KEYS.filter(
    (k) => !discarded.has(k.toLowerCase()),
  );

  // Create new query directly from sourceQuery filtered by allowed keys
  const baseFromSource: Record<string, string | string[]> = {};
  allowedKeys.forEach((key) => {
    const value = sourceQuery[key as keyof typeof sourceQuery];
    if (value == null) return;
    baseFromSource[key] = Array.isArray(value)
      ? (value as unknown[]).map((v) => String(v))
      : String(value as unknown);
  });

  // Handle string hrefs
  if (typeof href === 'string') {
    if (isExternalHref(href)) {
      // Do not append to mailto/tel regardless of appendToExternal
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return href;
      if (!appendToExternal) return href; // keep as-is for other external
      return buildExternalUrl(href, { extraParams: baseFromSource });
    }
    // On-domain Hugo blog shares our GA4 — forwarding internal-nav tracking
    // params would corrupt source attribution, so never forward them to it.
    const stringBase: Record<string, string | string[]> = isBlogPath(href)
      ? {}
      : baseFromSource;
    if (!asObject) {
      const search = new URLSearchParams();
      Object.entries(stringBase).forEach(([key, value]) => {
        if (Array.isArray(value)) value.forEach((v) => search.append(key, v));
        else search.append(key, value);
      });
      return `${href}${search.toString() ? `?${search.toString()}` : ''}`;
    }
    return { pathname: href, query: stringBase } as any;
  }

  // Handle UrlObject hrefs
  if (typeof href.pathname === 'string' && isExternalHref(href.pathname)) {
    // Do not append to mailto/tel regardless of appendToExternal
    if (
      href.pathname.startsWith('mailto:') ||
      href.pathname.startsWith('tel:')
    ) {
      return asObject ? href : href.pathname;
    }
    if (!appendToExternal) {
      // Return as string by default or object if requested
      if (asObject) return href;
      const incomingQuery = (href.query ?? {}) as Record<
        string,
        string | string[]
      >;
      return buildExternalUrl(href.pathname, {
        incomingQuery,
        appendHash: typeof href.hash === 'string' ? href.hash : undefined,
      });
    }
    const incomingQuery = (href.query ?? {}) as Record<
      string,
      string | string[]
    >;
    return buildExternalUrl(href.pathname, {
      incomingQuery,
      extraParams: baseFromSource,
      appendHash: typeof href.hash === 'string' ? href.hash : undefined,
    });
  }
  const incomingQuery = (href.query ?? {}) as Record<string, string | string[]>;
  // On-domain Hugo blog shares our GA4 — forwarding internal-nav tracking
  // params would corrupt source attribution, so never forward them to it.
  const objBase: Record<string, string | string[]> =
    typeof href.pathname === 'string' && isBlogPath(href.pathname)
      ? {}
      : baseFromSource;
  if (!asObject) {
    // Merge queries; for array keys present in both, union values, for scalars incoming overrides
    const finalQuery: Record<string, string | string[]> = {};
    const add = (k: string, v: string | string[]) => {
      if (Array.isArray(v)) {
        if (!Array.isArray(finalQuery[k])) finalQuery[k] = [] as string[];
        const existing = finalQuery[k] as string[];
        v.forEach((item) => {
          if (!existing.includes(item)) existing.push(item);
        });
      } else {
        finalQuery[k] = v;
      }
    };
    // start with the (blog-guarded) forwarded params
    Object.entries(objBase).forEach(([k, v]) => add(k, v));
    // then apply incomingQuery
    Object.entries(incomingQuery).forEach(([k, v]) => add(k, v));

    const search = new URLSearchParams();
    Object.entries(finalQuery).forEach(([key, value]) => {
      if (Array.isArray(value)) value.forEach((v) => search.append(key, v));
      else search.append(key, value);
    });
    const pathname = typeof href.pathname === 'string' ? href.pathname : '';
    const hash = typeof href.hash === 'string' ? normalizeHash(href.hash) : '';
    return `${pathname}${
      search.toString() ? `?${search.toString()}` : ''
    }${hash}`;
  }
  return { ...href, query: { ...objBase, ...incomingQuery } };
}

export function useFormatLink(defaultOptions?: {
  discardParams?: string[];
  appendToExternal?: boolean;
  asObject?: boolean;
}) {
  const searchParams = useSearchParams();
  const { regionSlug } = useRegionLocale();

  const sourceQuery = useMemo(() => {
    const query: Record<string, string | string[]> = {};
    if (!searchParams) return query;
    for (const key of searchParams.keys()) {
      const values = searchParams.getAll(key);
      query[key] = values.length > 1 ? values : values[0];
    }
    return query;
  }, [searchParams]);

  return useCallback(
    (
      href: LinkProps['href'],
      options?: {
        discardParams?: string[];
        appendToExternal?: boolean;
        asObject?: boolean;
      },
    ) => {
      const prefixedHref = addRegionPrefixToHref(href, regionSlug);
      return formatLink(prefixedHref, sourceQuery, {
        ...defaultOptions,
        ...options,
      });
    },
    [
      sourceQuery,
      regionSlug,
      defaultOptions?.discardParams,
      defaultOptions?.appendToExternal,
      defaultOptions?.asObject,
    ],
  );
}

function getSafeRelForBlankTarget(rel?: string): string {
  const tokens = new Set(
    String(rel ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean),
  );
  tokens.add('noopener');
  tokens.add('noreferrer');
  return Array.from(tokens).join(' ');
}

const CustomLink = React.forwardRef<HTMLAnchorElement, CustomLinkProps>(
  function CustomLink(props: CustomLinkProps, ref) {
    const {
      href,
      discardParams,
      appendToExternal = false,
      prefetch = false,
      ...rest
    } = props;

    const getFormatted = useFormatLink();
    const computedHref = useMemo<HrefType>(() => {
      return getFormatted(href, {
        discardParams,
        appendToExternal,
        asObject: true,
      }) as HrefType;
    }, [href, getFormatted, discardParams, appendToExternal]);

    let safeRel = rest.rel;
    if (rest.target === '_blank') {
      safeRel = getSafeRelForBlankTarget(rest.rel);
    }

    return (
      <Link
        ref={ref}
        href={computedHref}
        prefetch={prefetch}
        {...rest}
        rel={safeRel}
      />
    );
  },
);

export default CustomLink;
